import React, { useState, useEffect } from 'react';
import { Bell, Sun, Moon, Search, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import UniversalSearchModal from './UniversalSearchModal';
import BatcheetLogo from './BatcheetLogo';

const Header = ({ navigate }) => {
  const { currentUser, userProfile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [unreadNotifsCount, setUnreadNotifsCount] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const notifsQuery = query(
      collection(db, 'users', currentUser.uid, 'notifications'),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(notifsQuery, (snapshot) => {
      setUnreadNotifsCount(snapshot.size);
    }, (err) => console.error("Header notif error:", err));

    return () => unsubscribe();
  }, [currentUser]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-[#0b0f17]/85 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 transition-colors duration-200">
        <div className="max-w-xl mx-auto px-3.5 sm:px-4">
          <div className="flex justify-between items-center h-14 sm:h-15">
            {/* Logo */}
            <div
              onClick={() => navigate('home')}
              className="flex items-center gap-2 cursor-pointer group select-none"
            >
              <BatcheetLogo size={34} className="group-hover:scale-105 active:scale-95 transition-transform" />
              <div className="flex items-center">
                <span className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Batcheet
                </span>
                <span className="ml-1.5 text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20 tracking-wider">
                  24H
                </span>
              </div>
            </div>

            {/* Right Action Icons */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Universal Search Button */}
              <button
                onClick={() => setIsSearchOpen(true)}
                title="Search Batcheet"
                className="p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
              >
                <Search className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
              </button>

              {/* 1-Tap Dark / Light Mode Switch */}
              <button
                onClick={toggleTheme}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                className="p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-amber-500 dark:hover:text-amber-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
              >
                {isDark ? <Sun className="w-4.5 h-4.5 sm:w-5 sm:h-5" /> : <Moon className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-indigo-600" />}
              </button>

              {/* Notifications */}
              {currentUser && (
                <button
                  onClick={() => navigate('notifications')}
                  title="Notifications"
                  className="relative p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <Bell className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                  {unreadNotifsCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[15px] h-[15px] px-1 bg-rose-500 text-white font-bold text-[9px] flex items-center justify-center rounded-full shadow-sm">
                      {unreadNotifsCount > 9 ? '9+' : unreadNotifsCount}
                    </span>
                  )}
                </button>
              )}

              {/* Settings */}
              {currentUser && (
                <button
                  onClick={() => navigate('settings')}
                  title="Settings & Privacy"
                  className="p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <Settings className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                </button>
              )}

              {/* User Avatar */}
              {userProfile && (
                <div
                  onClick={() => navigate('profile', { userId: currentUser.uid })}
                  className="flex items-center pl-1 cursor-pointer group"
                >
                  <div className="w-8 h-8 sm:w-8.5 sm:h-8.5 rounded-full p-[1.5px] bg-gradient-to-tr from-sky-400 to-indigo-500 group-hover:scale-105 active:scale-95 transition-transform shadow-sm">
                    <img
                      src={userProfile.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser.uid}`}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Universal Search Modal */}
      <UniversalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        navigate={navigate}
      />
    </>
  );
};

export default Header;
